/**
 *  Component <e2-address-book> is used to display a customer's addresses.
 *
 * @attr {boolean} [is-selectable] determines if addresses are selectable in the component
 *
 * @prop {string} selectedAddressId stores the id of the address selected in the component
 *
 * example:
 * <e2-address-book is-selectable></e2-address-book>
 */

import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';

import { SimpleLitElement } from '@elab/components';
import { addressBookType, addressStoreName, addressTypes } from '@elab/core/utils/constants';
import connect from '@elab/core/store/connect';
import { actions as globalActions } from '@elab/core/store/global';
import { actions as addressActions, selectors as addressSelectors } from '@elab/core/store/address';
import { storeName } from '@elab/components/connected/forms/MyAddressesForm';
import { getActions as getAddressFormActions } from '@elab/components/connected/forms/MyAddressesForm/redux';
import { selectors as configSelectors } from '@elab/core/store/config';
import { button, link } from '@elab/core/utils/htmlTemplates';

class AddressBook extends SimpleLitElement {
    static get properties() {
        return {
            isSelectable: { type: Boolean, attribute: 'is-selectable' },
            selectedAddressId: { type: String, attribute: false },
            addressBookType: { type: String, attribute: 'address-book-type' },
            firstLabel: { type: String, attribute: 'first-label' },
            secondLabel: { type: String, attribute: 'second-label' },
            thirdLabel: { type: String, attribute: 'third-label' },
            fourthLabel: { type: String, attribute: 'fourth-label' },
            classOverride: { type: String, attribute: 'class-override' },
            hideLinks: { type: Boolean, attribute: 'hide-links' },
            addresses: { type: Array, attribute: false },
            billingAddresses: { type: Array, attribute: false },
            editAddressText: { type: String, attribute: 'edit-address-text' },
            editAddressURL: { type: String, attribute: 'edit-address-url' },
            removeAddressText: {
                type: String,
                attribute: 'remove-address-text',
            },
            showTitle: { type: Boolean, attribute: 'show-title' },
            showHouseNumberFirst: {
                type: Boolean,
                attribute: 'show-house-number-first',
            },
            showFormInPopup: { type: Boolean, attribute: 'show-form-in-popup' },
            selectDefaultCheckbox: {
                type: Boolean,
                attribute: 'select-default-as-checkbox',
            },
            showRemoveIcon: {
                type: Boolean,
                attribute: 'show-remove-icon',
            },
            shippingLabel: { type: String, attribute: 'shipping-label' },
            billingLabel: { type: String, attribute: 'billing-label' },
            splitForms: { type: Boolean, attribute: 'split-forms' },
            addAddressButtonText: { type: String, attribute: 'add-address-button-text' },
            chooseAddressButtonText: {
                type: String,
                attribute: 'choose-address-button-text',
            },
            analyticsConfig: { type: String, attribute: 'analytics-config' },
            individualAddressLabel: { type: String, attribute: 'individual-address-label' },
            corporateAddressLabel: { type: String, attribute: 'corporate-address-label' },
            buttonSubmitSize: { type: String, attribute: 'button-submit-size' },
            radioView: { type: Boolean, attribute: 'radio-view' },
            defaultBillingAddressId: { type: String, attribute: false },
            defaultDeliveryAddressId: { type: String, attribute: false },
            disableSelectAsDefault: { type: Boolean, attribute: 'disable-select-as-default' },
            setSelectedForPaymentAddresses: {
                type: Boolean,
                attribute: 'set-selected-for-payment-addresses',
            },
            useCartUrlForEdit: { type: Boolean, attribute: 'use-cart-url-for-edit' },
            useCartUrlForAdd: { type: Boolean, attribute: 'use-cart-url-for-add' },
            selectForPaymentAfterEditing: {
                type: Boolean,
                attribute: 'select-for-payment-after-editing',
            },
            submitEditMethod: { type: String, attribute: 'submit-edit-method' },
            isContextNav: { type: Boolean, attribute: 'is-context-nav' },
            separator: { type: String, attribute: 'separator' },
            restrictAddress: { type: Boolean, attribute: 'restrict-address' },
            useNeighborhood: { type: Boolean, attribute: 'use-neighborhood' },
            neighborhoodFieldInfo: { type: String, attribute: 'neighborhood-field-info' },
        };
    }

    constructor() {
        super();
        this.firstLabel = '';
        this.secondLabel = '';
        this.thirdLabel = '';
        this.fourthLabel = '';
        this.editAddressText = '';
        this.editAddressURL = '';
        this.removeAddressText = '';
        this.addressBookType = '';
        this.shippingLabel = '';
        this.billingLabel = '';
        this.splitForms = false;
        this.addAddressButtonText = '';
        this.individualAddressLabel = '';
        this.corporateAddressLabel = '';
        this.buttonSubmitSize = 'big';
        this.radioView = false;
        this.defaultDeliveryAddressId = '';
        this.defaultBillingAddressId = '';
        this.disableSelectAsDefault = false;
        this.setSelectedForPaymentAddresses = false;
        this.useCartUrlForEdit = false;
        this.useCartUrlForAdd = false;
        this.selectForPaymentAfterEditing = false;
        this.submitEditMethod = '';
        this.isContextNav = false;
        this.separator = ',';
        this.useNeighborhood = false;
        this.neighborhoodFieldInfo = '';
    }

    firstUpdated() {
        this.dispatchEvent(
            new CustomEvent('set-address-book-type', {
                detail: this.addressBookType,
            }),
        );
        if (this.addressBookType === addressBookType.BILLING) {
            this.dispatchEvent(new CustomEvent('load-billing-addresses'));
        } else if (this.addressBookType === addressBookType.FULL) {
            this.dispatchEvent(new CustomEvent('load-addresses'));
            this.dispatchEvent(new CustomEvent('load-billing-addresses'));
        } else {
            this.dispatchEvent(new CustomEvent('load-addresses'));
        }
        if (this.setSelectedForPaymentAddresses) {
            if (this.splitForms) {
                this.dispatchEvent(new CustomEvent('set-selected-for-payment-addresses'));
            }
        }
        if (this.useNeighborhood) {
            this.dispatchEvent(
                new CustomEvent('update-neighborhood-field-info', {
                    detail: this.neighborhoodFieldInfo,
                }),
            );
        }
    }

    renderRow = (label, value) => {
        return value
            ? html`
                  <p class="address-item__row">
                      ${label} ${value}
                  </p>
              `
            : '';
    };

    setAsDefault = (event, id) => {
        event.preventDefault();
        this.radioView && event.stopPropagation();
        this.dispatchEvent(new CustomEvent('set-as-default', { detail: id }));
    };

    handleRemove(event, id) {
        this.radioView && event.stopPropagation();
        this.dispatchEvent(new CustomEvent('remove-address', { detail: id }));
    }

    getStoreName(addressType) {
        if (!this.splitForms) {
            return;
        }
        if (addressType === addressBookType.SHIPPING_ADDRESS) {
            return addressStoreName.shipping;
        }
        if (addressType === addressBookType.BILLING_ADDRESS) {
            return addressStoreName.billing;
        }
    }

    handleEdit(event, id, addressType) {
        this.radioView && event.stopPropagation();
        const storeName = this.getStoreName(addressType);

        this.dispatchEvent(
            new CustomEvent('edit-address-in-popup', {
                detail: {
                    id,
                    storeName,
                    addressType,
                    radioView: this.radioView,
                    useCartUrlForEdit: this.useCartUrlForEdit,
                    useCartUrlForAdd: this.useCartUrlForAdd,
                    submitEditMethod: this.submitEditMethod,
                    selectForPaymentAfterEditing: this.selectForPaymentAfterEditing,
                },
            }),
        );
    }

    renderRemoveButton(id) {
        return html`
            <a
                class="address-item__link address-item__link--remove"
                @click="${e => this.handleRemove(e, id)}"
            >
                ${this.removeAddressText}
            </a>
        `;
    }

    renderAddAddressButton = ({ addressType }) => {
        const store = this.getStoreName(addressType) || storeName;
        return html`
            <div class="address-book__add-button">
                <e2-action-button
                    class-modifier="button button--add-address button--primary button--full-width"
                    no-arrow
                    action-type="${store}.OPEN_FORM_IN_DIALOG"
                    data-address-type=${addressType}
                    button-label="${this.addAddressButtonText}"
                    size="${this.buttonSubmitSize}"
                    data-radio-view="${this.radioView}"
                    data-use-cart-url-for-edit="${this.useCartUrlForEdit ? 'true' : ''}"
                    data-use-cart-url-for-add="${this.useCartUrlForAdd ? 'true' : ''}"
                    data-submit-edit-method="${this.submitEditMethod}"
                    data-select-for-payment-after-editing="${this.selectForPaymentAfterEditing}"
                >
                    <span class="address-book__add-button-icon"></span>
                </e2-action-button>
            </div>
        `;
    };

    handleClick(detail) {
        if (!this.isSelectable) return;
        this.dispatchEvent(new CustomEvent('set-selected-address-id', { detail }));
    }

    openRemoveConfirmationDialog(id) {
        this.dispatchEvent(
            new CustomEvent('show-remove-confirmation', {
                detail: {
                    id,
                },
            }),
        );
    }

    getClasses(id, defaultAddress, selectedAddressId) {
        return classMap({
            'address-item': true,
            'address-item--default': defaultAddress,
            'address-item--selected': id === (this.selectedAddressId || selectedAddressId),
            'address-item__radio': this.radioView,
        });
    }

    getEditUrl(id) {
        return this.editAddressURL.length ? `${this.editAddressURL}?id=${id}` : '#';
    }

    renderCompanyDetails({ companyCode, vatNumber, iban, bankName }) {
        return html`
            ${this.renderRow('', companyCode)} ${this.renderRow('', vatNumber)}
            ${this.renderRow('', iban)} ${this.renderRow('', bankName)}
        `;
    }

    renderItemDetails = (
        {
            id = null,
            title = '',
            firstName = '',
            middleName = '',
            lastName = '',
            line1 = '',
            line2 = '',
            pobox = '',
            postalCode = '',
            town = '',
            country = {},
            mobileNumber = '',
            phone = '',
            formattedPhone = '',
            defaultAddress = '',
            companyName = '',
            addressType: type = '',
            companyCode,
            vatNumber,
            iban,
            bankName,
            neighborhood = '',
        },
        isOnlyOne = true,
        addressType,
    ) => {
        const isCorporateAddress = type === addressTypes.CORPORATE;
        const showCorporateAddress = isCorporateAddress && this.showCorporateOptionFields;
        return html`
            ${!isOnlyOne && this.showRemoveIcon
                ? html`
                      <div
                          class="address-item__delete-button"
                          @click="${() => this.openRemoveConfirmationDialog(id)}"
                      >
                          <i class="icon icon-close"></i>
                      </div>
                  `
                : html``}
            <h1 class="address-item__title">
                ${showCorporateAddress
                    ? html`
                          ${companyName}
                      `
                    : html`
                          ${this.showTitle ? title : ''} ${firstName} ${middleName} ${lastName}
                      `}
            </h1>
            <div class="address-item__data">
                ${this.corporateAddressLabel && this.individualAddressLabel
                    ? html`
                          <p class="address-item__row">
                              ${isCorporateAddress
                                  ? this.corporateAddressLabel
                                  : this.individualAddressLabel}
                          </p>
                      `
                    : ''}

                <p class="address-item__row">
                    ${this.showHouseNumberFirst
                        ? html`
                              <span>${line2} ${pobox} ${line1}${this.separator || ''}</span>
                          `
                        : html`
                              <span>${line1} ${line2} ${pobox}${this.separator || ''}</span>
                          `}
                </p>

                ${neighborhood && this.useNeighborhood
                    ? html`
                          <p>
                              ${neighborhood}
                          </p>
                      `
                    : null}

                <p class="address-item__row address-item__row--city-country">
                    <span>
                        ${postalCode
                            ? html`
                                  <span
                                      class="address-item__postal-code ${this.separator
                                          ? ''
                                          : 'address-item__postal-code--no-separator'}"
                                  >
                                      ${postalCode}</span
                                  >
                              `
                            : ''}
                        <span class="address-item__city">${town}</span>
                    </span>
                    <span class="address-item__row-item address-item__row-item--country"
                        >${country.name || ''}</span
                    >
                </p>

                ${this.renderRow(this.firstLabel, mobileNumber)}
                ${this.renderRow(this.secondLabel, formattedPhone || phone)}
                ${showCorporateAddress
                    ? this.renderCompanyDetails({ companyCode, vatNumber, iban, bankName })
                    : this.renderRow('', companyName)}
            </div>

            ${defaultAddress
                ? html`
                      <div class="address-item__is-default-label">
                          <span class="address-item__is-default-icon"></span>
                          ${this.thirdLabel}
                      </div>
                  `
                : ''}
            ${this.hideLinks
                ? html``
                : html`
                      ${this.isContextNav
                          ? html`
                                <e2-tooltip
                                    .id="${id}"
                                    class="address-item__links-nav"
                                    without-close-button
                                >
                                    <i
                                        tabindex="1"
                                        class="icon icon-menu-vertical"
                                        v-slot="target"
                                    ></i>
                                    <div v-slot="content">
                                        ${this.getLinksTemplate(
                                            id,
                                            addressType,
                                            defaultAddress,
                                            isOnlyOne,
                                        )}
                                    </div>
                                </e2-tooltip>
                            `
                          : this.getLinksTemplate(id, addressType, defaultAddress, isOnlyOne)}
                  `}
        `;
    };

    getLinksTemplate = (id, addressType, defaultAddress, isOnlyOne) => html`
        <div class="address-item__links">
            <div class="address-item__edit-links">
                ${this.showFormInPopup
                    ? html`
                          ${this.analyticsConfig && this.analyticsConfig !== ''
                              ? html`
                                    <e2-click-tracking
                                        analytics-config="${this.analyticsConfig}"
                                        type="account-edit_address"
                                    >
                                        <a
                                            class="address-item__link"
                                            @click="${e => this.handleEdit(e, id, addressType)}"
                                        >
                                            ${this.editAddressText}
                                        </a>
                                    </e2-click-tracking>
                                `
                              : html`
                                    <a
                                        class="address-item__link"
                                        @click="${e => this.handleEdit(e, id, addressType)}"
                                    >
                                        ${this.editAddressText}
                                    </a>
                                `}
                      `
                    : html`
                          ${link(
                              {
                                  className: '',
                                  href: this.getEditUrl(id),
                              },
                              html`
                                  <span class="address-item__link">
                                      ${this.editAddressText}
                                  </span>
                              `,
                          )}
                      `}
                ${!isOnlyOne
                    ? html`
                          <span class="address-item__link-separator">|</span>
                      `
                    : html``}
                ${!isOnlyOne && !this.showRemoveIcon ? this.renderRemoveButton(id) : html``}
            </div>
            ${!this.disableSelectAsDefault
                ? html`
                      <div class="address-item__set-as-default">
                          ${this.selectDefaultCheckbox && !defaultAddress
                              ? html`
                                    <e2-form-toggler
                                        name="set-as-default"
                                        display-as-checkbox
                                        css-class="address-book__toggler"
                                        @click="${event => this.setAsDefault(event, id)}"
                                    >
                                        <div v-slot="toggler__label">
                                            <span data-target="label">
                                                ${this.fourthLabel}
                                            </span>
                                        </div>
                                    </e2-form-toggler>
                                `
                              : html`
                                    <a
                                        class="address-item__set-as-default-link"
                                        @click="${event => this.setAsDefault(event, id)}"
                                    >
                                        ${this.fourthLabel}
                                    </a>
                                `}
                      </div>
                  `
                : html``}
        </div>
    `;

    renderItem = (address, isOnlyOne = true, addressType) => html`
        <article
            @click="${() => this.handleClick(address.id)}"
            class="${this.getClasses(address.id, address.defaultAddress)}"
        >
            ${this.renderItemDetails(address, isOnlyOne, addressType)}
        </article>
    `;

    renderSelectableItem = (address, isOnlyOne = true, addressType) => {
        const { id, defaultAddress } = address;
        const defaultAddressId =
            this.addressBookType === addressBookType.BILLING
                ? this.defaultBillingAddressId
                : this.defaultDeliveryAddressId;

        const value = this.selectedAddressId || defaultAddressId;

        return html`
            <article class="${this.getClasses(id, defaultAddress, value)}">
                <div class="radio-field__container">
                    ${this.renderItemDetails(address, isOnlyOne, addressType)}
                </div>
                ${button(
                    {
                        className: 'button--full-width button--black button--text-center',
                        clickHandler: () => this.setCurrentAddressId(id),
                    },
                    this.chooseAddressButtonText,
                )}
            </article>
        `;
    };

    renderMultipleAddressBlocks = () => {
        return html`
            <h2 class="address-book__addresses-title">
                ${this.shippingLabel}
            </h2>
            <div class="address-book__wrapper">
                <div class="address-book__addresses-wrapper">
                    ${this.addresses &&
                        this.addresses.map(address =>
                            this.renderItem(
                                address,
                                this.addresses.length === 1,
                                addressBookType.SHIPPING_ADDRESS,
                            ),
                        )}
                </div>
                ${this.analyticsConfig && this.analyticsConfig !== ''
                    ? html`
                          <e2-click-tracking
                              analytics-config="${this.analyticsConfig}"
                              type="account-add_address"
                          >
                              ${this.renderAddAddressButton({
                                  addressType: addressBookType.SHIPPING_ADDRESS,
                              })}
                          </e2-click-tracking>
                      `
                    : this.renderAddAddressButton({
                          addressType: addressBookType.SHIPPING_ADDRESS,
                      })}
            </div>
            <h2 class="address-book__addresses-title">
                ${this.billingLabel}
            </h2>
            <div class="address-book__wrapper">
                <div class="address-book__addresses-wrapper">
                    ${this.billingAddresses &&
                        this.billingAddresses.map(address =>
                            this.renderItem(
                                address,
                                this.billingAddresses.length === 1,
                                addressBookType.BILLING_ADDRESS,
                            ),
                        )}
                </div>
                ${this.analyticsConfig && this.analyticsConfig !== ''
                    ? html`
                          <e2-click-tracking
                              analytics-config="${this.analyticsConfig}"
                              type="account-add_address"
                              >${this.renderAddAddressButton({
                                  addressType: addressBookType.BILLING_ADDRESS,
                              })}
                          </e2-click-tracking>
                      `
                    : this.renderAddAddressButton({
                          addressType: addressBookType.BILLING_ADDRESS,
                      })}
            </div>
        `;
    };

    setCurrentAddressId = detail => {
        if (this.addressBookType === addressBookType.BILLING) {
            this.dispatchEvent(new CustomEvent('set-current-billing-address-id', { detail }));
        } else {
            this.dispatchEvent(new CustomEvent('set-current-delivery-address-id', { detail }));
        }
    };

    renderAsRadio = addressSource => {
        const addressType =
            this.addressBookType === addressBookType.BILLING
                ? addressBookType.BILLING_ADDRESS
                : addressBookType.SHIPPING_ADDRESS;
        return html`
            ${this.renderAddAddressButton({ addressType })}
            ${addressSource.map(address =>
                this.renderSelectableItem(address, addressSource.length === 1, addressType),
            )}
        `;
    };

    renderAddresses = () => {
        const addressSource =
            this.addressBookType === addressBookType.BILLING
                ? this.billingAddresses
                : this.addresses;

        if (this.radioView) {
            return this.renderAsRadio(addressSource);
        }
        if (this.addressBookType === addressBookType.FULL) {
            return this.renderMultipleAddressBlocks();
        }
        return addressSource.map(address => this.renderItem(address, this.addresses.length === 1));
    };

    render() {
        return html`
            <div class="${this.classOverride || 'address-book'}">
                ${this.renderAddresses()}
            </div>
        `;
    }
}

const mapStateToProps = (state, ctx) => ({
    addresses: addressSelectors.getAddresses(state),
    billingAddresses: addressSelectors.getBillingAddresses(state),
    selectedAddressId:
        addressSelectors.getSelectedAddressId(state) ||
        (ctx.addressBookType === addressBookType.BILLING
            ? addressSelectors.getCurrentBillingAddressId(state)
            : addressSelectors.getCurrentDeliveryAddressId(state)),
    defaultBillingAddressId: addressSelectors.getDefaultBillingAddressId(state),
    defaultDeliveryAddressId: addressSelectors.getDefaultAddressId(state),
    showCorporateOptionFields: configSelectors.getShowCorporateOptionFields(state),
});

const mapDispatchToEvents = (dispatch, ctx) => ({
    'load-addresses': () =>
        dispatch(addressActions.loadAddresses({ restrictAddress: ctx.restrictAddress })),
    'load-billing-addresses': () => dispatch(addressActions.loadBillingAddresses()),
    'set-as-default': ({ detail }) => dispatch(addressActions.setAsDefault({ id: detail })),
    'remove-address': ({ detail }) => dispatch(addressActions.removeAddress(detail)),
    'set-selected-address-id': ({ detail }) =>
        dispatch(addressActions.setSelectedAddressId(detail)),
    redirect: e => dispatch(globalActions.redirect(e.detail)),
    'show-remove-confirmation': ({ detail }) =>
        dispatch(addressActions.removeWithConfirmation(detail)),
    'set-address-book-type': ({ detail }) => dispatch(addressActions.setAddressBookType(detail)),
    'edit-address-in-popup': ({
        detail: {
            id,
            storeName: customStoreName,
            addressType,
            radioView,
            useCartUrlForEdit,
            useCartUrlForAdd,
            submitEditMethod,
            selectForPaymentAfterEditing,
        },
    }) =>
        dispatch(
            getAddressFormActions(customStoreName || storeName).openFormInDialog({
                id,
                addressType,
                radioView,
                useCartUrlForEdit,
                useCartUrlForAdd,
                submitEditMethod,
                selectForPaymentAfterEditing,
            }),
        ),
    'set-current-delivery-address-id': ({ detail }) =>
        dispatch(addressActions.setAddressForPayment({ id: detail, isDelivery: true })),
    'set-current-billing-address-id': ({ detail }) =>
        dispatch(addressActions.setAddressForPayment({ id: detail, isDelivery: false })),
    'set-selected-for-payment-addresses': () =>
        dispatch(addressActions.setSelectedForPaymentAddresses()),
    'update-neighborhood-field-info': ({ detail }) =>
        dispatch(addressActions.setNeighborhoodFieldInfo(detail)),
});

export default () =>
    customElements.define(
        'e2-address-book',
        connect(mapStateToProps, mapDispatchToEvents)(AddressBook),
    );
